import Checkbox from 'components/Widget/CheckboxItem';
import Select from 'components/Widget/Select';
import { Tier } from 'dragonmouse-shared/interfaces';
import { useCallback, useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { setSettings, setSettingsModalVisible } from 'redux/mode/actions';
import { DarkModeBehavior } from 'redux/mode/reducers';
import { selectIsGuest, selectUser, selectUserSettings } from 'redux/db/reducers';
import { RootReducer } from 'redux/reducers';
import Modal from '..';
import * as S from './styles';
import { toast } from 'react-toastify';
import forge from 'apis/forge';

const SettingsModal = () => {
	const dispatch = useDispatch();

	const {
		darkModeBehavior,
		connectingLinesEnabled,
		figmaIntegrationEnabled,
		visualEditsEnabled,
		designSystemsEnabled,
		subscription,
		user,
		isGuest,
	} = useSelector((r: RootReducer) => {
		const userSettings = selectUserSettings(r.db);
		return {
			darkModeBehavior: r.mode.settings.darkModeBehavior,
			connectingLinesEnabled: r.mode.settings.connectingLinesEnabled,
			figmaIntegrationEnabled: r.mode.settings.figmaIntegrationEnabled,
			visualEditsEnabled: userSettings.visualEditsEnabled,
			designSystemsEnabled: userSettings.designSystemsEnabled,
			subscription: r.db.subscription,
			user: selectUser(r.db),
			isGuest: selectIsGuest(r.db),
		};
	}, shallowEqual);

	const close = useCallback(() => {
		dispatch(setSettingsModalVisible(false));
	}, [dispatch]);

	const handleVisualEditsToggle = useCallback(() => {
		if (!user || isGuest) {
			toast.error('Please sign in to enable Visual Edits');
			return;
		}

		forge.updateUser({
			update: {
				id: user.id,
				metadata: {
					settings: {
						...user.metadata?.settings,
						visualEditsEnabled: !visualEditsEnabled,
					},
				},
			},
		});
	}, [user, isGuest, visualEditsEnabled]);

	const handleDesignSystemsToggle = useCallback(() => {
		if (!user || isGuest) {
			toast.error('Please sign in to enable Design Systems');
			return;
		}

		forge.updateUser({
			update: {
				id: user.id,
				metadata: {
					settings: {
						...user.metadata?.settings,
						designSystemsEnabled: !designSystemsEnabled,
					},
				},
			},
		});
	}, [user, isGuest, designSystemsEnabled]);

	// when command , is pressed, close modal
	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.metaKey && event.key === ',') {
				event.preventDefault();
				close();
			}
		};
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [close]);

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------
	const renderDropdown = (
		value: string,
		onChange: (value: any) => void,
		options: Array<{ value: string; label: string }>,
		label: string
	) => {
		return (
			<S.Setting>
				<S.SettingRow>
					<S.Spacer />
					<S.Choice>
						<S.DropdownContainer>
							<Select maxWidth={130} onChange={onChange} value={value}>
								{options.map((option) => (
									<option key={option.value} value={option.value}>
										{option.label}
									</option>
								))}
							</Select>
						</S.DropdownContainer>
					</S.Choice>
					<S.Label>{label}</S.Label>
				</S.SettingRow>
			</S.Setting>
		);
	};

	const renderCheckbox = (
		checked: boolean,
		onToggle: () => void,
		label: string,
		disabled?: boolean,
		detailText?: string
	) => {
		const handleToggle = disabled ? () => {} : onToggle;
		return (
			<S.Setting>
				<S.SettingRow>
					<S.Spacer />
					<S.Choice>
						<S.CheckboxContainer style={disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}>
							<Checkbox width={16} height={16} checked={checked} onToggled={handleToggle} />
						</S.CheckboxContainer>
					</S.Choice>
					<S.Label
						onClick={handleToggle}
						style={disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
					>
						{label}
					</S.Label>
				</S.SettingRow>
				{detailText && (
					<S.DescriptionRow>
						<S.Spacer />
						<S.Description>{detailText}</S.Description>
					</S.DescriptionRow>
				)}
			</S.Setting>
		);
	};

	const isFreeTier = (subscription?.tier ?? Tier.FREE) === Tier.FREE;

	return (
		<Modal title="Preferences" cancelLabel="Close" submitLabel="Save" onClose={close}>
			<S.Content>
				<S.FirstSectionHeader>This Device</S.FirstSectionHeader>
				{renderDropdown(
					darkModeBehavior,
					(event: any) =>
						dispatch(setSettings({ darkModeBehavior: event.target.value as DarkModeBehavior })),
					Object.values(DarkModeBehavior).map((behavior) => ({
						value: behavior,
						label: behavior,
					})),
					'Dark mode behavior'
				)}
				{renderCheckbox(
					connectingLinesEnabled,
					() => dispatch(setSettings({ connectingLinesEnabled: !connectingLinesEnabled })),
					'Connecting lines'
				)}
				{renderCheckbox(
					figmaIntegrationEnabled,
					() => dispatch(setSettings({ figmaIntegrationEnabled: !figmaIntegrationEnabled })),
					'Figma integration (experimental)'
				)}
				<S.SectionHeader>All Devices</S.SectionHeader>
				{renderCheckbox(
					designSystemsEnabled,
					handleDesignSystemsToggle,
					'Design Systems 2.0',
					isGuest,
					'Enabling this disables legacy design systems (e.g. "Design System" panel in scene)'
				)}
				{renderCheckbox(
					visualEditsEnabled,
					handleVisualEditsToggle,
					'Visual Edits Alpha (Pro/Max only)',
					isGuest || isFreeTier
				)}
			</S.Content>
		</Modal>
	);
};

export default SettingsModal;
